/**
 * Per-notification-type tab deep-link.
 *
 * Some notifications should open their entity's detail page on a specific tab
 * rather than the default one — a new ticket message lands on the Messages tab,
 * a comment mention on the Comments tab, and a certification-expiry alert on the
 * employee's Skills tab, so what triggered the alert is immediately visible.
 *
 * The mapping is CONFIGURED, not coded: each event declares an optional
 * `deep_link_tab` in `notification_events.config.json` — the same catalog the
 * preferences matrix and notification titles already render from, and the same
 * file the backend email CTA reads
 * (`NotificationEmailService._apply_deep_link_tab`,
 * shared/notifications/email_delivery.py). Adding a deep link is therefore a
 * one-line config change that reaches every surface at once (bell list, web
 * toast / native handler, mobile push, email) instead of two hand-synced switch
 * statements in two languages.
 */
import notificationEventsConfig from '../../shared-config/notification_events.config.json';

interface RawEvent {
  notification_type?: string;
  deep_link_tab?: string;
}
interface RawCategory {
  events?: RawEvent[];
}
interface RawModule {
  categories?: RawCategory[];
}
interface RawConfig {
  modules?: Record<string, RawModule>;
}

/**
 * `notification_type` → tab key, built once from the catalog.
 *
 * A type shared by sibling events (e.g. `message.new` for DM and channel
 * messages) shares a destination, so the first declaration wins — matching the
 * backend's `NotificationCatalog.deep_link_tabs()`. Siblings declaring
 * DIFFERENT tabs for one type is a config error; the backend logs it (both ends
 * read this same file, so a conflict is caught there rather than duplicated
 * here).
 */
function buildDeepLinkTabMap(): Record<string, string> {
  const tabs: Record<string, string> = {};
  for (const module of Object.values((notificationEventsConfig as RawConfig).modules ?? {})) {
    for (const category of module.categories ?? []) {
      for (const event of category.events ?? []) {
        if (event.notification_type && event.deep_link_tab && !tabs[event.notification_type]) {
          tabs[event.notification_type] = event.deep_link_tab;
        }
      }
    }
  }
  return tabs;
}

const DEEP_LINK_TABS: Record<string, string> = buildDeepLinkTabMap();

/** Tab key to deep-link to for a given notification type, or null for none. */
export function notificationDeepLinkTab(notificationType: string | undefined): string | null {
  if (!notificationType) return null;
  return DEEP_LINK_TABS[notificationType] ?? null;
}

/**
 * Set the deep-link `tab=` param for a notification type on an entity URL.
 * Returns the URL unchanged when the type has no tab mapping.
 *
 * The notification's tab REPLACES any `tab=` the entity template already carries
 * (a `urlContextTemplate` can pin one — e.g. a chatbot channel resolves to
 * `/chatbot/bots/{bot_id}?tab=channels`), rather than being appended alongside
 * it: two `tab=` params would leave the destination page picking whichever it
 * reads first. Other query params on the template are preserved.
 */
export function applyNotificationDeepLinkTab(
  url: string,
  notificationType: string | undefined,
): string {
  const tab = notificationDeepLinkTab(notificationType);
  if (!tab) return url;
  const [path, query] = url.split('?');
  const params = new URLSearchParams(query);
  params.set('tab', tab);
  return `${path}?${params.toString()}`;
}
